/*
 * A promotion's terms, written from its settings and never typed. A copy of
 * the admin's `frontend/src/core/promotions/terms.ts` (mpgadmin), so a
 * client reads the same words the owner saw when saving it. Change both
 * together. Pure: no clock, no screen.
 */
import type { PromotionAudience, PromotionDiscountKind } from '../types/promotion.types';

export interface PromotionTermsInput {
  discountKind: PromotionDiscountKind;
  discountValue: number;
  minSpend?: number | null;
  /** null = no limit per client (mpgadmin D91). */
  usesPerClient: number | null;
  audience: PromotionAudience;
  rebookWithinWeeks?: number | null;
  rewardEveryNthVisit?: number | null;
  allLocations: boolean;
  locationNames?: string[];
  endsOn?: string | null;
  /** Its ways to pay, in the server's words (mpgadmin D92): "Cash only". */
  waysText?: string | null;
  /** "pay what's left in cash or by bank transfer". */
  waysTerms?: string | null;
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "2026-12-31" → "31 Dec 2026"; with year false → "31 Dec". */
export const dayText = (iso: string, year = true): string => {
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return iso;
  return year ? `${d} ${MONTHS[m - 1]} ${y}` : `${d} ${MONTHS[m - 1]}`;
};

/** "$15", "$7.50". */
export const money = (n: number): string => (Number.isInteger(n) ? `$${n}` : `$${n.toFixed(2)}`);

/** "once each", "up to 3 bookings", "no limit per client" (null: no limit, mpgadmin D91). */
const usesTerm = (n: number | null, unit: string): string =>
  n == null ? 'no limit per client' : n <= 1 ? 'once each' : `up to ${n} ${unit}`;

const trimNumber = (n: number) => (Number.isInteger(n) ? String(n) : String(Number(n.toFixed(2))));

/** "$15 off" / "10% off". */
export const offText = (p: Pick<PromotionTermsInput, 'discountKind' | 'discountValue'>): string =>
  p.discountKind === 2 ? `${trimNumber(p.discountValue)}% off` : `${money(p.discountValue)} off`;

/** 2 → "2nd", 4 → "4th", 11 → "11th". */
export const ordinal = (n: number): string => {
  const t = n % 100;
  if (t >= 11 && t <= 13) return `${n}th`;
  return `${n}${['th', 'st', 'nd', 'rd'][n % 10] ?? 'th'}`;
};

export const listText = (names: string[]) =>
  names.length <= 1 ? (names[0] ?? '') : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;

/**
 * "$15 off · up to 3 bookings · $30 minimum spend · new clients · All
 * locations · until 31 Dec 2026 · one promotion per booking · not on packs"
 */
export const termsLine = (p: PromotionTermsInput): string => {
  const parts: string[] = [];
  if (p.rebookWithinWeeks && p.rewardEveryNthVisit) {
    parts.push(
      `${offText(p)} every ${ordinal(p.rewardEveryNthVisit)} visit in a row, each booked within ${p.rebookWithinWeeks} weeks of the last groom; a missed window starts the count again`
    );
    parts.push(usesTerm(p.usesPerClient, 'rewards'));
  } else if (p.rebookWithinWeeks) {
    parts.push(`${offText(p)} every visit booked within ${p.rebookWithinWeeks} weeks of the last groom`);
    parts.push(usesTerm(p.usesPerClient, 'visits'));
  } else {
    parts.push(offText(p));
    parts.push(usesTerm(p.usesPerClient, 'bookings'));
  }
  // Its ways to pay, in the server's words (mpgadmin D92), where the admin's terms put them.
  if (p.waysTerms?.trim()) parts.push(p.waysTerms.trim());
  if (p.minSpend && p.minSpend > 0) parts.push(`${money(p.minSpend)} minimum spend`);
  if (p.audience === 1) parts.push('new clients');
  if (p.audience === 2) parts.push('returning clients');
  parts.push(p.allLocations || !p.locationNames?.length ? 'All locations' : `${listText(p.locationNames)} only`);
  parts.push(p.endsOn ? `until ${dayText(p.endsOn)}` : 'no end date');
  parts.push('one promotion per booking', 'not on packs');
  return parts.join(' · ');
};

/** The short line under a ticket's title: "For 3 bookings · $30 min · ends 31 Dec". */
export const summaryLine = (p: PromotionTermsInput): string => {
  const parts: string[] = [];
  if (p.rebookWithinWeeks) parts.push(`Every ${p.rebookWithinWeeks} weeks`);
  if (p.rebookWithinWeeks && p.rewardEveryNthVisit) parts.push(`${ordinal(p.rewardEveryNthVisit)} visit`);
  parts.push(
    p.usesPerClient == null
      ? 'No limit'
      : p.usesPerClient <= 1
        ? 'Once each'
        : `For ${p.usesPerClient} ${p.rebookWithinWeeks ? (p.rewardEveryNthVisit ? 'rewards' : 'visits') : 'bookings'}`
  );
  if (p.minSpend && p.minSpend > 0) parts.push(`${money(p.minSpend)} min`);
  if (p.waysText?.trim()) parts.push(p.waysText.trim());
  if (!p.allLocations && p.locationNames?.length) parts.push(`${listText(p.locationNames)} only`);
  parts.push(p.endsOn ? `ends ${dayText(p.endsOn, false)}` : 'no end date');
  return parts.join(' · ');
};
